import React, { useEffect } from 'react';
import Accordion, { AccordionProps } from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { TbIcon } from 'components/Figma';

export interface AccordionItemProps {
  id: string;
  title: React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  actions?: React.ReactNode;
}

interface CustomAccordionProps {
  items: AccordionItemProps[];
  defaultExpandedId?: string;
  onChange?: (expandedId: string | false) => void;
  alwaysKeepOneExpanded?: boolean;
}

const StyledAccordion = styled(({ key, expanded, onChange, defaultExpanded, className, children }: AccordionProps) => (
  <Accordion
    disableGutters
    elevation={0}
    square
    key={key}
    expanded={expanded}
    onChange={onChange}
    defaultExpanded={defaultExpanded}
    className={className}
  >
    {children}
  </Accordion>
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: '#fff',
  marginBottom: '8px',
  '&:before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    minHeight: '44px',
    height: '44px',
    padding: '0 16px',
    display: 'flex',
    alignItems: 'center',
  },
  '&.Mui-expanded .MuiAccordionSummary-root': {
    backgroundColor: '#F7F7F7',
  },
}));

export function CustomAccordion({
  items,
  defaultExpandedId = '',
  onChange = () => {},
  alwaysKeepOneExpanded = false,
}: CustomAccordionProps) {
  const [expanded, setExpanded] = React.useState<string | false>(defaultExpandedId || false);

  const didInit = React.useRef(false);

  useEffect(() => {
    if (alwaysKeepOneExpanded && !didInit.current) {
      setExpanded(defaultExpandedId);
      didInit.current = true;
    }
  }, [alwaysKeepOneExpanded, defaultExpandedId]);

  const handleChange = (panelId: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    if (alwaysKeepOneExpanded && !isExpanded && expanded === panelId) {
      return;
    }

    const newExpandedId = isExpanded ? panelId : false;
    setExpanded(newExpandedId);
    onChange?.(newExpandedId);
  };

  return (
    <div className="w-full">
      {items.map(item => (
        <StyledAccordion
          key={item.id}
          expanded={expanded === item.id}
          onChange={handleChange(item.id)}
          defaultExpanded={item.defaultExpanded}
          className="shadow-none w-full"
        >
          <AccordionSummary
            expandIcon={<TbIcon name="ChevronDown" className="lg" id={`${item.id}-expand-icon`} />}
            aria-controls={`${item.id}-content`}
            id={`${item.id}-header`}
            className="w-full px-4"
            style={{ minHeight: '44px', height: '44px' }}
          >
            <Typography component="span" sx={{ width: '100%' }}>
              {item.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails className="w-full px-4 py-2 bg-white">{item.children}</AccordionDetails>
          {item.actions && <AccordionActions className="w-full">{item.actions}</AccordionActions>}
        </StyledAccordion>
      ))}
    </div>
  );
}
